import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
	DefaultMatterAdjustmentTenancyCurrent,
	MatterAdjustmentTenancyCurrent,
} from '@models/matter';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { cloneDeep } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AdjustmentData } from '../adjustments.types';
import { ICON } from '@core/util/icon';
import { calculateTenancyCurrent } from '../../../../shared/services/adjustments';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AdjustmentsSelector } from '@state/matter/adjustments/adjustments.selectors';
import { AdjustmentsDispatch } from '@state/matter/adjustments';
import { NgForm } from '@angular/forms';
import { createId } from '@core/util/file';
import { AdjustmentDefaultDetailsSelectors } from '@state/global/adjustment-details';

@UntilDestroy()
@Component({
	selector: 'app-tenancy-current',
	templateUrl: './tenancy-current-dialog.component.html',
	standalone: false,
})
export class TenancyCurrentDialogComponent implements OnInit {
	submitted = false;
	adjustment: MatterAdjustmentTenancyCurrent = {
		...cloneDeep(DefaultMatterAdjustmentTenancyCurrent),
		id: createId(),
	};
	adjustmentDate: string;
	public maxStartDate: string;
	public maxStartDateLabel: string;
	@Select(SelectedMatter.adjustmentDate) adjustmentDate$: Observable<string>;
	@SelectSnapshot(SelectedMatter.matterHST) hst: number;
	areaItems = [
		{
			label: 'Silent',
			value: 'silent',
		},
		{
			label: 'Specify',
			value: 'specify',
		},
	];

	@Select(SelectedMatter.closingDate) closingDate$: Observable<string>;
	creditAmount: number;
	hstAmount: number;
	readonly ICON = ICON;
	@Select(AdjustmentsSelector.getAdjustment)
	tenancyCurrentAdjustmentFilterFn$: Observable<
		(id: string) => MatterAdjustmentTenancyCurrent
	>;

	vendorCollectedItems = [
		{
			label: 'Yes',
			value: 'yes',
		},
		{
			label: 'No',
			value: 'no',
		},
	];

	vendorDays: number;
	vendorShare: number;

	@SelectSnapshot(
		AdjustmentDefaultDetailsSelectors.adjustmentDetailsTenancyCurrent,
	)
	public adjustmentDetails: boolean;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: AdjustmentData,
		private dialogRef: MatDialogRef<TenancyCurrentDialogComponent>,
		private adjustmentDispatch: AdjustmentsDispatch,
	) {}

	ngOnInit(): void {
		this.adjustment = {
			...cloneDeep(DefaultMatterAdjustmentTenancyCurrent),
			id: createId(),
			details: this.adjustmentDetails,
		};
		this.creditAmount = 0;
		combineLatest([
			this.closingDate$,
			this.adjustmentDate$, // this order is important, `adjustmentDate` should override `closingDate` if it exists
		])
			.pipe(
				untilDestroyed(this),
				map((dates) => dates.filter((date) => !!date).pop()),
			)
			.subscribe((date) => {
				this.adjustmentDate = date;
				this.changed();
			});

		this.getTenancyCurrent();
	}

	getTenancyCurrent = () => {
		this.tenancyCurrentAdjustmentFilterFn$
			.pipe(
				untilDestroyed(this),
				map((fn) => fn(this.data?.adjustmentId)),
			)
			.subscribe((_adj) => {
				if (_adj) {
					this.adjustment = cloneDeep(_adj);
					this.changed();
				}
			});
	};

	cancelModal(): void {
		this.dialogRef.close();
	}

	changed(): void {
		const calculation = calculateTenancyCurrent(
			this.adjustment.rent,
			this.adjustment.applyHst,
			this.adjustmentDate,
			this.adjustment.rentalEnd,
			this.adjustment.rentalStart,
			this.adjustment.vendorCollected,
			this.hst,
		);

		this.creditAmount = calculation.creditAmount;
		this.hstAmount = calculation.hstAmount;
		this.vendorDays = calculation.vendorDays;
		this.vendorShare = calculation.vendorShare;

		if (this.adjustmentDate && this.adjustment.rentalEnd) {
			if (this.adjustmentDate > this.adjustment.rentalEnd) {
				this.maxStartDate = this.adjustmentDate;
				this.maxStartDateLabel = 'Adjustment Date';
			} else {
				this.maxStartDate = this.adjustment.rentalEnd;
				this.maxStartDateLabel = 'the End Date';
			}
		} else if (this.adjustmentDate) {
			this.maxStartDate = this.adjustmentDate;
			this.maxStartDateLabel = 'Adjustment Date';
		} else if (this.adjustment.rentalEnd) {
			this.maxStartDate = this.adjustment.rentalEnd;
			this.maxStartDateLabel = 'the End Date';
		}
	}

	onSubmit(form: NgForm): void {
		this.submitted = true;
		if (form.valid) {
			this.adjustmentDispatch.Set(this.adjustment);
			this.dialogRef.close();
		}
	}
}
